import {FC, useCallback, useState, useEffect} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useBulkEnrollUsersInPlaylist} from "@/app2/shared/data";
import {palette} from "@/constants";
import {FormControlLabel, Radio, RadioGroup, Stack} from "@mui/material";
import {ENROLLMENT_TYPES, EnrollmentType} from "@buildwitt/shapeshift-types";
import InfoIcon from "@mui/icons-material/Info";
import {CustomTooltip} from "@/components/common";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";

interface IProps {
	open: boolean;
	onClose: () => void;
	selectedUserIds: number[];
	playlistId: number;
}

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

const AdminBulkPlaylistEnrollmentConfirmationModal: FC<IProps> = ({open, onClose, selectedUserIds, playlistId}) => {
	const {t} = useSSTranslation();
	const {successSnackbar, errorSnackbar} = useCustomSnackbar();
	const bulkEnrollMutation = useBulkEnrollUsersInPlaylist(playlistId);

	const [enrollmentType, setEnrollmentType] = useState<EnrollmentType>(ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE);

	useEffect(() => {
		if (!open) {
			setEnrollmentType(ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE);
		}
	}, [open]);

	const handleConfirm = useCallback(() => {
		if (!open) return;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(bulkEnrollMutation.mutate as (vars: any, opts: any) => void)(
			{
				userIds: selectedUserIds,
				enrollmentType: ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE,
			},
			{
				onSuccess: () => {
					successSnackbar(t("bulk_playlist_enrollment_success", "Successfully enrolled users in playlist"));
					onClose();
				},
				onError: (error: Error) => {
					errorSnackbar(t("bulk_playlist_enrollment_error", `Failed to enroll users: ${error.message}`));
				}
			}
		);
	}, [open, selectedUserIds, bulkEnrollMutation, successSnackbar, errorSnackbar, t, onClose]);

	const isConfirmDisabled = bulkEnrollMutation.isPending;

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="alert-dialog-title">
				{t("bulk_playlist_enrollment_modal_title", "Enroll Users in Playlist", {count: selectedUserIds.length})}
			</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<DialogContentText id="alert-dialog-description" variant="body1" color="text.primary">
						{t(
							"bulk_playlist_enrollment_modal_body_copy",
							"This will enroll the selected users in the playlist and all of its courses. Choose how you want to schedule this playlist in their training feed."
						)}
					</DialogContentText>
					<RadioGroup
						row
						value={enrollmentType}
						onChange={(e) => setEnrollmentType(e.target.value as EnrollmentType)}
						aria-labelledby="enrollment-type-group-label"
					>
						<FormControlLabel
							value={ENROLLMENT_TYPES.PRIORITIZE_SCHEDULE}
							control={<Radio />}
							label={
								<Stack direction="row" gap={1}>
									{t("bulk_enrollment_prioritize_option", "Prioritize in Training Feed")}
									<CustomTooltip title={t("prioritized_enrollment_tooltip_copy", "Prioritized enrollment will start on users next available day")}>
										<InfoIcon color="disabled" />
									</CustomTooltip>
								</Stack>
							}
						/>
					</RadioGroup>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx} disabled={bulkEnrollMutation.isPending}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleConfirm}
					sx={buttonSx}
					disabled={isConfirmDisabled}
					startIcon={bulkEnrollMutation.isPending ? <CircularProgress size={16} /> : undefined}
				>
					{bulkEnrollMutation.isPending
						? t("common_enrolling", "Enrolling\u2026")
						: t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AdminBulkPlaylistEnrollmentConfirmationModal;
